import React, { useContext, useEffect, useState } from "react";
import SelectTheater from "./SelectTheater";
import axios from "axios";
import * as Constants from "../api/constants";
import { Context } from "../App";
import "../partyBossTheme.css";
import { normalizeTheatersList } from "../utils/normalizeTheaters";
import { toDateInputValue } from "../utils/eventDate";
import { persistAddonMeta } from "../utils/bookingFlowStorage";
import { normalizeAddonMetaPayload } from "../utils/normalizeAddonMeta";
import { enrichSlotFromTheatre } from "../utils/slotGuests";
import { getFirstSelectableSlotIndex } from "../utils/slotAvailability";
import {
  fetchTheatreGalleryByNames,
  fetchTheatreExperienceByNames,
  fetchTheatreVideosByNames,
} from "../utils/theaterGalleryImages";
import { mergeSlotWithOffer } from "../utils/slotTimeOffers";

/**
 * Step 1: pick date + theatre + slot (same data load as legacy BookNow).
 */
export default function BookNowTheaterStep() {
  const [theatersLoading, setTheatersLoading] = useState(true);
  const [theatersError, setTheatersError] = useState(null);
  const [bookingTheaters, setBookingTheaters] = useState([]);

  const {
    setTheatersList,
    setAddonMetaData,
    context,
    SetSelectedTime,
    setTheatreGalleryByKey,
    setTheatreVideoByKey,
    setTheatreExperienceByKey,
  } = useContext(Context);

  const dateKey = toDateInputValue(context?.eventDate);

  useEffect(() => {
    document.body.classList.add("party-boss-home");
    return () => document.body.classList.remove("party-boss-home");
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const evDate = { date: dateKey };

    setTheatersLoading(true);
    setTheatersError(null);

    axios
      .all([
        axios.post(Constants.GET_THEATERS_LIST, evDate, {
          signal: controller.signal,
        }),
        axios.get(Constants.GET_META_DATA_ADDONS, {
          signal: controller.signal,
        }),
      ])
      .then(
        axios.spread((firstResponse, secondResponse) => {
          const raw = firstResponse?.data;
          const list = normalizeTheatersList(raw);

          setBookingTheaters(list);
          setTheatersList(list);
          const meta = normalizeAddonMetaPayload(secondResponse?.data ?? {});
          setAddonMetaData(meta);
          persistAddonMeta(meta);

          const names = list
            .map((t) => t?.name || t?.theatreName || t?.theaterName || t?.theatre)
            .filter(Boolean);
          Promise.all([
            fetchTheatreGalleryByNames(names),
            fetchTheatreVideosByNames(names),
            fetchTheatreExperienceByNames(names),
          ]).then(([byKey, byVideoKey, byExperienceKey]) => {
            setTheatreGalleryByKey(byKey);
            setTheatreVideoByKey(byVideoKey);
            setTheatreExperienceByKey(byExperienceKey || {});
          });

          const firstTheatre = list[0];
          const slotIx = getFirstSelectableSlotIndex(
            firstTheatre?.timeslots,
            dateKey
          );
          const firstSlot =
            slotIx >= 0 ? firstTheatre?.timeslots?.[slotIx] : null;
          if (firstSlot) {
            const enriched = enrichSlotFromTheatre(firstSlot, firstTheatre);
            SetSelectedTime(
              mergeSlotWithOffer(
                enriched,
                firstTheatre.id,
                dateKey,
                meta.slotOffers ?? []
              )
            );
          }
        })
      )
      .catch((error) => {
        if (
          controller.signal.aborted ||
          error?.code === "ERR_CANCELED" ||
          error?.name === "CanceledError"
        ) {
          return;
        }
        console.error("BookNow API:", error?.response?.data || error?.message);
        setTheatersError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load theatres. Please try again."
        );
        setBookingTheaters([]);
        setTheatersList([]);
        setTheatreGalleryByKey({});
        setTheatreVideoByKey({});
        setTheatreExperienceByKey({});
      })
      .finally(() => {
        if (!controller.signal.aborted) setTheatersLoading(false);
      });

    return () => controller.abort();
  }, [
    dateKey,
    setAddonMetaData,
    setTheatersList,
    SetSelectedTime,
    setTheatreGalleryByKey,
    setTheatreVideoByKey,
    setTheatreExperienceByKey,
  ]);

  return (
    <SelectTheater
      theatersLoading={theatersLoading}
      theatersError={theatersError}
      theaters={bookingTheaters}
    />
  );
}
